import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ObjectBadge } from '@/components/apps/CellValue';
import { recordTitle } from '@/components/apps/app-records-model';
import { type AppCollection, type AppRecord, editAppRecordCellsById } from '@/lib/app-records';
import { outreachAction } from '@/lib/outreach';
import { cn } from '@/lib/utils';
import { EMAIL_MISSING_LABELS, EMAIL_STATUS_LABELS, ENROLLMENT_LABELS, formatWhen } from './outreach-labels';
import { OutreachProspectDetail } from './OutreachProspectDetail';
import { PeopleSearchCell } from './PeopleSearchCell';
import type { OutreachData } from './useOutreach';

interface OutreachProspectsProps {
  toolAppId: string;
  /** The campaign open: its objects only, its sender's gestures. */
  sequenceId: string;
  isOwner: boolean;
  collection: AppCollection;
  records: AppRecord[];
  data: OutreachData;
  /** Parameters added by hand: filled here, cell by cell, as in lemlist's lead list. */
  manualColumns: Array<{ key: string; label: string }>;
  /** The list's other columns, read as the app's runs wrote them. */
  listColumns: Array<{ key: string; label: string }>;
  canManage: boolean;
  onCellSaved: () => void;
}

const SKIP_LABELS: Record<string, string> = {
  no_contact: 'sans personne',
  no_email: 'sans e-mail',
  opposed: 'refus de contact',
  already_in_sequence: 'déjà en séquence',
  in_other_campaign: 'déjà dans une autre campagne',
  missing: 'paramètre manquant',
};

/**
 * The prospects of a campaign, one row per person (lemlist's « leads »): the
 * person to write to, their address and its state, where the sequence stands.
 * A selection finds people, finds addresses, then prepares the e-mails, or
 * leaves the campaign.
 */
export const OutreachProspects = ({ toolAppId, sequenceId, isOwner, collection, records, data, manualColumns, listColumns, canManage, onCellSaved }: OutreachProspectsProps) => {
  const [newColumn, setNewColumn] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const live = useMemo(() => records.filter((record) => !record.archivedAt), [records]);
  const target = data.campaign?.settings.target ?? '';
  const chosen = live.filter((record) => selected.has(record.id));

  const run = async (body: Record<string, unknown>, say: (result: Record<string, unknown>) => string) => {
    setWorking(true);
    setNotice(null);
    try {
      setNotice(say(await outreachAction(body)));
      await data.reload();
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'Action impossible.');
    } finally {
      setWorking(false);
    }
  };
  const withoutEmail = chosen.map((record) => data.primaryOf(record.id)).filter((contact) => contact && !contact.email && contact.emailMissing !== 'opposed');

  const valueOf = (record: AppRecord, key: string) => {
    const value = record.computed.find((cell) => cell.key === key)?.value;
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  };
  const saveCell = (record: AppRecord, key: string, value: string) => {
    if (value.trim() === valueOf(record, key).trim()) return;
    void editAppRecordCellsById(record.id, record.version, { [key]: value.trim() || null }, 'manual', null)
      .then(onCellSaved)
      .catch((cause: unknown) => setNotice(cause instanceof Error ? cause.message : 'Valeur non enregistrée.'));
  };

  const addColumn = (label: string) => {
    if (!label.trim()) { setNewColumn(null); return; }
    void outreachAction({ action: 'add_parameter', toolAppId, label })
      .then(() => { setNewColumn(null); onCellSaved(); })
      .catch((cause: unknown) => setNotice(cause instanceof Error ? cause.message : 'Paramètre non ajouté.'));
  };
  const span = 7 + listColumns.length + manualColumns.length;

  const toggleAll = (on: boolean) => setSelected(on ? new Set(live.map((record) => record.id)) : new Set());

  return (
    <div className="space-y-3">
      {chosen.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-3 py-2">
          <span className="text-sm text-domino-ink">{chosen.length} sélectionnée{chosen.length > 1 ? 's' : ''}</span>
          <Button size="sm" variant="secondary" disabled={working}
            onClick={() => { void run({ action: 'find_people', toolAppId, recordIds: chosen.map((record) => record.id), target }, (result) => `${Number(result.queued ?? 0)} recherche(s) lancée(s)`); }}>
            Trouver qui contacter
          </Button>
          {withoutEmail.length > 0 && (
            <Button size="sm" variant="secondary" disabled={working}
              onClick={() => { void run({ action: 'find_email', contactIds: withoutEmail.map((contact) => contact!.id) }, (result) => `${Number(result.queued ?? 0)} recherche(s) d’e-mail lancée(s)`); }}>
              Trouver l’e-mail
            </Button>
          )}
          {isOwner && <Button size="sm" disabled={working}
            onClick={() => {
              void run({ action: 'prepare', sequenceId, recordIds: chosen.map((record) => record.id) }, (result) => {
                const skipped = Array.isArray(result.skipped) ? result.skipped as Array<{ reason: string; missing?: string[]; campaign?: string }> : [];
                const reasons = [...new Set(skipped.map((item) => item.reason === 'missing' && item.missing?.length
                  ? `manque ${item.missing.join(', ')}`
                  : item.reason === 'in_other_campaign' && item.campaign ? `déjà dans « ${item.campaign} »` : SKIP_LABELS[item.reason] ?? item.reason))];
                return `${Number(result.prepared ?? 0)} à relire${skipped.length ? ` · ${skipped.length} laissée(s) : ${reasons.join(' ; ')}` : ''}`;
              });
            }}>
            Préparer les e-mails
          </Button>}
          {isOwner && <Button size="sm" variant="ghost" disabled={working}
            onClick={() => {
              void run({ action: 'remove_from_campaign', sequenceId, recordIds: chosen.map((record) => record.id) }, (result) => {
                setSelected(new Set());
                return `${Number(result.removed ?? 0)} retirée(s) de la campagne`;
              });
            }}>
            Retirer de la campagne
          </Button>}
          <button type="button" className="ml-auto text-xs text-domino-inkSoft hover:text-domino-aubergine" onClick={() => toggleAll(false)}>Désélectionner</button>
        </div>
      )}
      {notice && <p role="status" className="text-xs text-domino-inkSoft">{notice}</p>}

      <div className="overflow-x-auto rounded-xl border border-domino-cardBorder bg-domino-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-domino-cardBorder text-xs text-domino-inkSoft">
            <tr>
              <th className="w-10 px-3 py-2">
                <input type="checkbox" aria-label="Tout sélectionner" checked={live.length > 0 && chosen.length === live.length} onChange={(event) => toggleAll(event.target.checked)} />
              </th>
              <th className="px-3 py-2 font-medium">{collection.columns[0]?.label ?? 'Entreprise'}</th>
              <th className="px-3 py-2 font-medium">Prénom</th>
              <th className="px-3 py-2 font-medium">Nom</th>
              <th className="px-3 py-2 font-medium">Poste</th>
              <th className="px-3 py-2 font-medium">E-mail</th>
              {listColumns.map((column) => <th key={column.key} className="px-3 py-2 font-medium">{column.label}</th>)}
              {manualColumns.map((column) => <th key={column.key} className="px-3 py-2 font-medium">{column.label}</th>)}
              <th className="px-3 py-2 font-medium">Séquence</th>
              <th className="w-10 px-2 py-2">
                {canManage && (newColumn === null
                  ? <button type="button" aria-label="Ajouter une colonne" onClick={() => setNewColumn('')} className="rounded-md p-1 text-domino-inkSoft hover:bg-domino-plumTint hover:text-domino-aubergine"><Plus className="h-4 w-4" /></button>
                  : <input autoFocus aria-label="Nom de la colonne" value={newColumn} onChange={(event) => setNewColumn(event.target.value)}
                      onBlur={(event) => addColumn(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') setNewColumn(null); }}
                      className="w-32 rounded-md border border-domino-cardBorder px-2 py-1 text-xs font-normal text-domino-ink focus:border-domino-aubergine focus:outline-none" />)}
              </th>
            </tr>
          </thead>
          <tbody>
            {live.flatMap((record) => {
              // One row per person, as lemlist's leads: every chosen person of the object, else its best one.
              const chosen = data.contactsOf(record.id).filter((contact) => contact.state === 'chosen');
              const people = chosen.length > 0 ? chosen : [data.primaryOf(record.id)];
              return people.map((contact, index) => ({ record, contact, first: index === 0, last: index === people.length - 1 }));
            }).map(({ record, contact, first, last }) => {
              const enrollment = contact ? data.inCampaign.find((item) => item.foundContactId === contact.id) ?? null : data.enrollmentOf(record.id);
              const elsewhere = !enrollment && contact ? data.liveElsewhere(contact.id) : null;
              const next = enrollment?.messages.find((message) => message.status === 'scheduled');
              const sentCount = enrollment?.messages.filter((message) => message.status === 'sent').length ?? 0;
              const isOpen = open === record.id;
              return (
                <Fragment key={`${record.id}-${contact?.id ?? 'none'}`}>
                  <tr className={cn('hover:bg-domino-warm', last && 'border-b border-domino-cardBorder/60', isOpen && 'bg-domino-warm')}>
                    <td className="px-3 py-2">
                      {first && <input type="checkbox" aria-label={`Sélectionner ${recordTitle(record, collection)}`} checked={selected.has(record.id)}
                        onChange={(event) => setSelected((current) => {
                          const nextSet = new Set(current);
                          if (event.target.checked) nextSet.add(record.id); else nextSet.delete(record.id);
                          return nextSet;
                        })} />}
                    </td>
                    <td className="px-3 py-2">
                      {first && <button type="button" onClick={() => setOpen(isOpen ? null : record.id)} className="flex items-center gap-2 text-left font-semibold text-domino-ink">
                        {isOpen ? <ChevronDown className="h-4 w-4 text-domino-inkSoft" /> : <ChevronRight className="h-4 w-4 text-domino-inkSoft" />}
                        <ObjectBadge name={recordTitle(record, collection)} />
                        <span className="truncate">{recordTitle(record, collection)}</span>
                      </button>}
                    </td>
                    <td className="px-3 py-2 text-domino-ink">
                      {contact ? contact.fullName.trim().split(/\s+/)[0] : (
                        <PeopleSearchCell recordId={record.id} data={data} working={working}
                          onRetry={() => { void run({ action: 'find_people', toolAppId, recordIds: [record.id], target }, () => 'Recherche relancée'); }} />
                      )}
                    </td>
                    <td className="px-3 py-2 text-domino-ink">{contact?.fullName.trim().split(/\s+/).slice(1).join(' ')}</td>
                    <td className="px-3 py-2 text-xs text-domino-inkSoft">{contact?.jobTitle}</td>
                    <td className="px-3 py-2">
                      {contact?.email
                        ? <span className="text-domino-ink">{contact.email}{contact.emailStatus && <span className="block text-xs text-domino-inkSoft">{EMAIL_STATUS_LABELS[contact.emailStatus]}</span>}</span>
                        : contact && (data.busy('find_email', contact.id)
                          ? <span className="text-xs text-domino-inkSoft">Recherche…</span>
                          : contact.emailMissing
                            ? <span className="text-xs text-domino-inkSoft">{EMAIL_MISSING_LABELS[contact.emailMissing]}</span>
                            : data.failed('find_email', contact.id) && <span className="text-xs text-domino-inkSoft">Échec de la recherche</span>)}
                    </td>
                    {listColumns.map((column) => (
                      <td key={column.key} className="max-w-[14rem] truncate px-3 py-2 text-domino-ink" title={valueOf(record, column.key)}>{valueOf(record, column.key)}</td>
                    ))}
                    {manualColumns.map((column) => (
                      <td key={column.key} className="px-2 py-1.5">
                        <input key={`${record.id}-${record.version}`} defaultValue={valueOf(record, column.key)} aria-label={`${column.label} · ${recordTitle(record, collection)}`}
                          onBlur={(event) => saveCell(record, column.key, event.target.value)}
                          onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                          className="w-full min-w-[10rem] rounded-md border border-transparent bg-transparent px-2 py-1 text-sm text-domino-ink hover:border-domino-cardBorder focus:border-domino-aubergine focus:bg-domino-white focus:outline-none" />
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      {elsewhere && <span className="whitespace-nowrap text-xs text-domino-inkSoft">Dans « {elsewhere} »</span>}
                      {enrollment && (
                        <span className="text-domino-ink">
                          {ENROLLMENT_LABELS[enrollment.status]}
                          <span className="block text-xs text-domino-inkSoft">
                            {sentCount}/{enrollment.messages.length}{next ? ` · ${formatWhen(next.scheduledFor)}` : ''}{next?.error ? ` · ${next.error}` : ''}
                          </span>
                        </span>
                      )}
                    </td>
                    <td />
                  </tr>
                  {isOpen && last && (
                    <tr className="border-b border-domino-cardBorder/60">
                      <td colSpan={span} className="p-0"><OutreachProspectDetail recordId={record.id} data={data} onError={setNotice} /></td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
